import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import {
  Chip,
  Divider,
  Flex,
  IconChevronDown,
  IconRefresh,
  Typography,
} from '@banhala/ui-kit';
import { useAtom } from 'jotai';
import { marketRankingFilterAtom } from '../states/marketRankingFilter.atom';
import { useMarketRankingTypes } from '../queries/useMarketRankingTypes';
import { useMarketRankingFilterOptions } from '../queries/useMarketRankingFilterOptions';
import type { MarketRankingType } from 'shared/src/api';
import { isNil, isNotNil } from 'shared/src/utils';
import { useAgeSelectBottomSheet } from './AgeSelectBottomSheet';
import { useCategoryStyleSelectBottomSheet } from './CategoryStyleSelectBottomSheet';

/**
 * 마켓 타입 선택 (스크롤과 함께 넘어감)
 * ListHeaderComponent로 사용
 */
export const MarketTypeHeader = () => {
  const { data: typesData } = useMarketRankingTypes();
  const [filter, setFilter] = useAtom(marketRankingFilterAtom);

  const selectedMarketType =
    filter.selectedMarketType ?? typesData.market_types[0] ?? null;

  const handleSelectType = useCallback(
    (type: MarketRankingType) => {
      setFilter({
        selectedMarketType: type,
        selectedCategorySno: null,
        selectedAgeValues: [],
        selectedStyles: [],
      });
    },
    [setFilter],
  );

  return (
    <Flex>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 12 }}
      >
        <Flex.Row gap={8}>
          {typesData.market_types.map(type => {
            const selected = type.sno === selectedMarketType?.sno;
            return (
              <Flex
                key={type.sno}
                padding={{ horizontal: 6, vertical: 4 }}
                onPress={() => handleSelectType(type)}
              >
                <Typography
                  type="h5"
                  color={selected ? 'content_primary' : 'content_tertiary'}
                >
                  {type.name}
                </Typography>
              </Flex>
            );
          })}
        </Flex.Row>
      </ScrollView>
      <Divider size="small" />
    </Flex>
  );
};

/**
 * 필터 칩 (연령, 카테고리, 스타일, 초기화)
 * List 밖에서 sticky로 사용
 */
export const MarketRankingFilterChips = ({
  marketTypeSno,
}: {
  marketTypeSno: number;
}) => {
  const { show: showAgeSelectBottomSheet } = useAgeSelectBottomSheet();
  const { show: categoryStyleSelectBottomSheet } =
    useCategoryStyleSelectBottomSheet();

  const { data } = useMarketRankingFilterOptions(marketTypeSno);
  const [filter, setFilter] = useAtom(marketRankingFilterAtom);

  const hasFilter =
    filter.selectedCategorySno !== null ||
    filter.selectedAgeValues.length > 0 ||
    filter.selectedStyles.length > 0;

  const handleReset = useCallback(() => {
    setFilter(prev => ({
      ...prev,
      selectedCategorySno: null,
      selectedAgeValues: [],
      selectedStyles: [],
    }));
  }, [setFilter]);

  const handleToggleAge = useCallback(() => {
    showAgeSelectBottomSheet({
      selected: filter.selectedAgeValues[0] ?? null,
      marketTypeSno,
    }).then(result => {
      if (result.type !== 'confirm') return;

      const value = result.data.nextValue;
      setFilter(prev => ({
        ...prev,
        selectedAgeValues: [value],
      }));
    });
  }, [
    filter.selectedAgeValues,
    marketTypeSno,
    setFilter,
    showAgeSelectBottomSheet,
  ]);

  const handleToggleCategoryStyle = useCallback(() => {
    // setFilter(prev => ({
    //   ...prev,
    //   selectedCategorySno: prev.selectedCategorySno === sno ? null : sno,
    // }));
    categoryStyleSelectBottomSheet({
      selected: {
        category: filter.selectedCategorySno,
        style: filter.selectedStyles,
      },
      marketTypeSno,
    }).then(result => {
      if (result.type !== 'confirm') {
        return;
      }
      setFilter(prev => ({
        ...prev,
        selectedCategorySno: result.data.nextValue.category,
        selectedStyles: result.data.nextValue.style,
      }));
    });
  }, [
    categoryStyleSelectBottomSheet,
    filter.selectedCategorySno,
    filter.selectedStyles,
    marketTypeSno,
    setFilter,
  ]);

  return (
    <Flex backgroundColor="white">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 12 }}
      >
        <Flex.Row gap={8}>
          {hasFilter && (
            <Chip
              size="medium"
              onPress={handleReset}
              state={'active'}
              rightIcon={<IconRefresh />}
            >
              초기화
            </Chip>
          )}
          <Chip
            onPress={handleToggleAge}
            size="medium"
            state={filter.selectedAgeValues.length > 0 ? 'selected' : 'enabled'}
            rightIcon={<IconChevronDown />}
          >
            {filter.selectedAgeValues.length === 0
              ? '전체 연령'
              : data.ages.filter(
                  age => age.value === filter.selectedAgeValues[0],
                )[0].name}
          </Chip>
          <Chip
            onPress={handleToggleCategoryStyle}
            size="medium"
            rightIcon={<IconChevronDown />}
            state={
              isNotNil(filter.selectedCategorySno) ? 'selected' : 'enabled'
            }
          >
            {isNil(filter.selectedCategorySno)
              ? '카테고리'
              : data.category_options.filter(
                  category => category.sno === filter.selectedCategorySno,
                )[0].name}
          </Chip>
          {data.styles.length > 0 && (
            <Chip
              onPress={handleToggleCategoryStyle}
              size="medium"
              rightIcon={<IconChevronDown />}
              state={filter.selectedStyles.length > 0 ? 'selected' : 'enabled'}
            >
              {filter.selectedStyles.length === 0
                ? '스타일'
                : `스타일 ${filter.selectedStyles.length}`}
            </Chip>
          )}
        </Flex.Row>
      </ScrollView>
      <Divider size="small" />
    </Flex>
  );
};
